"use client";

import { useState } from "react";
import { motion } from "framer-motion";

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",      // Deep Teal Green - Company color
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  white: "#FFFFFF",        // White
  whiteOff: "#F8F9FA",     // Off-white for subtle contrast
  textDark: "#1A2E29",     // Dark text for contrast
};

// BulletPoint component with TypeScript interface
interface BulletPointProps {
  children: React.ReactNode;
  number?: number;
}

const BulletPoint = ({ children, number }: BulletPointProps) => (
  <li className="relative pl-6 mb-2">
    {number ? (
      <span className="absolute left-0 top-0 flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: colors.primaryLight }}>
        {number}
      </span>
    ) : (
      <span className="absolute left-0 top-2 h-2 w-2 rounded-full" style={{ backgroundColor: colors.primaryLight }}></span>
    )}
    {children}
  </li>
);

// FAQ Data
const faqItems = [
  {
    question: "What makes our loan services different from traditional banks?",
    answer: (
      <>
        <p className="mb-4">
          Our financial solutions focus on transparency and customer-centric features:
        </p>
        <ul className="space-y-2">
          <BulletPoint>
            <strong>Flexible terms</strong> - Customized repayment schedules
          </BulletPoint>
          <BulletPoint>
            <strong>Competitive rates</strong> - Lower-than-average interest rates
          </BulletPoint>
          <BulletPoint>
            <strong>Clear pricing</strong> - No hidden fees or charges
          </BulletPoint>
        </ul>
      </>
    ),
  },
  {
    question: "How are interest rates calculated?",
    answer: (
      <>
        <p className="mb-4">
          We use simple interest calculation methods:
        </p>
        <ol className="space-y-3">
          <BulletPoint number={1}>
            <strong>Fixed rates</strong>: Consistent percentage throughout loan term
          </BulletPoint>
          <BulletPoint number={2}>
            <strong>Variable rates</strong>: Tied to market indexes with caps
          </BulletPoint>
          <BulletPoint number={3}>
            <strong>Discount options</strong>: Reduced rates for automatic payments
          </BulletPoint>
        </ol>
      </>
    ),
  },
  {
    question: "What are the eligibility requirements?",
    answer: (
      <>
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <h4 className="mb-3 font-semibold flex items-center" style={{ color: colors.primary }}>
              <svg className="mr-2 h-5 w-5" style={{ color: colors.primaryLight }} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
              For Individuals
            </h4>
            <ul className="space-y-2">
              <BulletPoint>Minimum 21 years of age</BulletPoint>
              <BulletPoint>Stable income source</BulletPoint>
              <BulletPoint>Good credit history</BulletPoint>
              <BulletPoint>Local residency</BulletPoint>
            </ul>
          </div>
          <div>
            <h4 className="mb-3 font-semibold flex items-center" style={{ color: colors.primary }}>
              <svg className="mr-2 h-5 w-5" style={{ color: colors.primaryLight }} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v1a2 2 0 012 2v8a2 2 0 01-2 2h-8a2 2 0 01-2-2v-8a2 2 0 012-2V4z" clipRule="evenodd" />
              </svg>
              For Businesses
            </h4>
            <ul className="space-y-2">
              <BulletPoint>1+ years in operation</BulletPoint>
              <BulletPoint>Positive cash flow</BulletPoint>
              <BulletPoint>Business license</BulletPoint>
              <BulletPoint>Financial statements</BulletPoint>
            </ul>
          </div>
        </div>
      </>
    ),
  },
  {
    question: "What documentation is required?",
    answer: (
      <>
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <h4 className="mb-3 font-semibold" style={{ color: colors.primary }}>Personal Loans</h4>
            <ul className="space-y-2">
              <BulletPoint>Government-issued ID</BulletPoint>
              <BulletPoint>Income verification</BulletPoint>
              <BulletPoint>Credit report</BulletPoint>
              <BulletPoint>Proof of address</BulletPoint>
            </ul>
          </div>
          <div>
            <h4 className="mb-3 font-semibold" style={{ color: colors.primary }}>Business Loans</h4>
            <ul className="space-y-2">
              <BulletPoint>Business registration</BulletPoint>
              <BulletPoint>Bank statements</BulletPoint>
              <BulletPoint>Tax returns</BulletPoint>
              <BulletPoint>Financial projections</BulletPoint>
            </ul>
          </div>
        </div>
      </>
    ),
  },
  {
    question: "What is the approval timeline?",
    answer: (
      <>
        <div className="flex items-start mb-4">
          <div className="mr-4 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: `${colors.primaryLight}20`, color: colors.primary }}>
            <span className="text-xl font-bold">24h</span>
          </div>
          <div>
            <h4 className="font-semibold" style={{ color: colors.primary }}>Fast Processing</h4>
            <p className="text-sm" style={{ color: colors.textDark }}>For complete applications</p>
          </div>
        </div>
        <ul className="space-y-2">
          <BulletPoint><strong>Standard approval</strong>: 1-3 business days</BulletPoint>
          <BulletPoint><strong>Express service</strong>: Available for qualified applicants</BulletPoint>
          <BulletPoint><strong>Complex cases</strong>: 5-7 business days</BulletPoint>
        </ul>
      </>
    ),
  },
  {
    question: "What if I miss a payment?",
    answer: (
      <>
        <p className="mb-4">
          We offer flexible solutions for financial difficulties:
        </p>
        <ol className="space-y-3">
          <BulletPoint number={1}>
            <strong>Payment extensions</strong>: Available once per year
          </BulletPoint>
          <BulletPoint number={2}>
            <strong>Revised schedules</strong>: Adjust repayment terms
          </BulletPoint>
          <BulletPoint number={3}>
            <strong>Fee waivers</strong>: Case-by-case consideration
          </BulletPoint>
        </ol>
        <div className="mt-4 rounded-lg p-4" style={{ backgroundColor: `${colors.primary}10`, border: `1px solid ${colors.primary}20` }}>
          <p className="text-sm" style={{ color: colors.textDark }}>
            <strong>Note</strong>: Repeated defaults may impact credit rating
          </p>
        </div>
      </>
    ),
  },
];

export default function GeneralFAQ() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <section className="py-20" style={{ backgroundColor: colors.white }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16 text-center"
        >
          <span className="mb-4 inline-block rounded-full px-6 py-2 text-sm font-semibold uppercase tracking-wider" style={{ backgroundColor: `${colors.primary}10`, color: colors.primary }}>
            FAQ
          </span>
          <h2 className="text-4xl font-bold sm:text-5xl" style={{ color: colors.textDark }}>
            Frequently Asked
            <span className="bg-gradient-to-r bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}>
              {" "}Questions
            </span>
          </h2>
          <div className="mx-auto mt-6 h-1.5 w-24 rounded-full" style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}></div>
          <p className="mx-auto mt-6 max-w-2xl text-lg" style={{ color: colors.textDark }}>
            Get answers to common questions about our financial services
          </p>
        </motion.div>
        {/* FAQ Accordion */}
        <div className="mx-auto max-w-3xl space-y-4">
          {faqItems.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05, duration: 0.3 }}
              viewport={{ once: true }}
              className="overflow-hidden rounded-2xl border shadow-sm transition-all hover:shadow-md"
              style={{ borderColor: `${colors.primary}20`, backgroundColor: colors.white }}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="flex w-full items-center justify-between p-6 text-left transition-colors hover:opacity-80"
              >
                <h3 className="text-lg font-semibold md:text-xl" style={{ color: colors.textDark }}>
                  <span className="mr-3 inline-block h-6 w-6 rounded-full text-center text-white" style={{ backgroundColor: colors.primary }}>
                    {index + 1}
                  </span>
                  {item.question}
                </h3>
                <span className="ml-4 h-6 w-6 flex-shrink-0" style={{ color: colors.primary }}>
                  <svg
                    className={`transform transition-transform duration-300 ${
                      activeIndex === index ? "rotate-180" : ""
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </span>
              </button>

              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{
                  height: activeIndex === index ? "auto" : 0,
                  opacity: activeIndex === index ? 1 : 0,
                }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="px-6 pb-6" style={{ color: colors.textDark }}>
                  {typeof item.answer === 'string' ? (
                    <p>{item.answer}</p>
                  ) : (
                    <div className="space-y-4">
                      {item.answer}
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Help Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.3 }}
          viewport={{ once: true }}
          className="mt-20 text-center"
        >
          <div className="inline-block rounded-2xl p-0.5 shadow-lg" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})` }}>
            <div className="rounded-2xl px-8 py-8 sm:px-12 sm:py-10" style={{ backgroundColor: colors.white }}>
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full text-3xl" style={{ backgroundColor: `${colors.primary}10`, color: colors.primary }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  className="h-8 w-8"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 10a1 1 0 011-1h6a1 1 0 011 1v1a1 1 0 01-1 1H9a1 1 0 01-1-1v-1z"
                  />
                </svg>
              </div>
              <h3 className="mt-6 text-2xl font-bold" style={{ color: colors.textDark }}>
                Need more information?
              </h3>
              <p className="mt-3" style={{ color: colors.textDark }}>
                Our financial experts are available to assist you
              </p>
              <button 
                className="mt-6 rounded-lg px-8 py-3 font-medium text-white shadow-md transition-all hover:shadow-lg hover:opacity-90"
                style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryLight})` }}
              >
                Contact Support
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}